import React, { useState, useEffect, useRef } from 'react';
import { X, Search, CheckCircle, AlertCircle, Package, Loader2, Info, Calendar } from 'lucide-react';
import { PListLineDto } from '../../../../../types/picking';
import { stcTransService } from '../../../../../services/stcTransService';
import { ScanTextInput, NumericInput } from '../../../../common/inputs';
import { useTranslation } from '../../../../../contexts/I18nProvider';

interface ExtendedPickingLine extends PListLineDto {
  itemDescription?: string;
}

interface BatchPickModalProps {
  isOpen: boolean;
  line: ExtendedPickingLine | null;
  pListId: number;
  onClose: () => void;
  onBatchPicked: (batchNo: string, quantity: number, expiryDate?: string | null) => void;
}

interface BatchItem {
  batchNo: string;
  expiryDate: string | null;
  quantity: number;
  binCode: string;
  warehouseId: string;
}

export const BatchPickModal: React.FC<BatchPickModalProps> = ({
  isOpen,
  line,
  pListId,
  onClose,
  onBatchPicked
}) => {
  const { t } = useTranslation();
  const [availableBatches, setAvailableBatches] = useState<BatchItem[]>([]);
  const [filteredBatches, setFilteredBatches] = useState<BatchItem[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedBatch, setSelectedBatch] = useState<string | null>(null);
  const [pickQuantity, setPickQuantity] = useState<number>(0);
  // Rå input-streng for antallsfeltet — parseFloat i onChange på en kontrollert
  // number-verdi ville fjernet desimaltegnet mens man taster (f.eks. «2.» → «2»)
  const [pickQuantityInput, setPickQuantityInput] = useState('0');
  const [confirming, setConfirming] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Synk input-strengen når pickQuantity endres programmatisk (reset ved åpning,
  // auto-utfylling ved batch-valg, clamp mot maks) — men la den stå urørt mens
  // brukeren taster en streng som allerede tilsvarer tallet (bevarer «2.» underveis)
  useEffect(() => {
    if ((parseFloat(pickQuantityInput) || 0) !== pickQuantity) {
      setPickQuantityInput(String(pickQuantity));
    }
  }, [pickQuantity, pickQuantityInput]);

  // Load available batches when modal opens
  useEffect(() => {
    if (isOpen && line) {
      loadAvailableBatches();
      setSearchTerm('');
      setSelectedBatch(null);
      setPickQuantity(0);
      setError(null);
    }
  }, [isOpen, line]);

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Filter batches when search term changes
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredBatches(availableBatches);
    } else {
      const term = searchTerm.toLowerCase();
      setFilteredBatches(
        availableBatches.filter(b => b.batchNo.toLowerCase().includes(term))
      );
    }
  }, [searchTerm, availableBatches]);

  // When a batch is selected, set default pick quantity
  useEffect(() => {
    if (selectedBatch && line) {
      const batch = availableBatches.find(b => b.batchNo === selectedBatch);
      if (batch) {
        const remaining = line.qty - line.qtyPicked;
        // Pick the minimum of remaining needed or available in batch
        setPickQuantity(Math.min(remaining, batch.quantity));
      }
    }
  }, [selectedBatch, availableBatches, line]);

  const loadAvailableBatches = async () => {
    if (!line?.itemNo || !line?.binCode || !line?.warehouseID) {
      setError(t('warehouse.modules.picking.batchPick.missingItemInfo'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const batches = await stcTransService.getBatchesAtBin(
        line.itemNo,
        line.warehouseID,
        line.binCode
      );
      setAvailableBatches(batches);
      setFilteredBatches(batches);

      // Pre-select requested batch from rule engine if it matches an available batch
      if (line.requestedBatchNo && batches.length > 0) {
        const match = batches.find(
          b => b.batchNo.toLowerCase() === line.requestedBatchNo!.toLowerCase()
        );
        if (match) {
          setSelectedBatch(match.batchNo);
        }
      }

      if (batches.length === 0) {
        setError(t('warehouse.modules.picking.batchPick.noBatchesAtBin'));
      }
    } catch (err: any) {
      console.error('Error loading batches:', err);
      setError(err.message || t('warehouse.modules.picking.batchPick.couldNotLoadBatches'));
    } finally {
      setLoading(false);
    }
  };

  const handleSelectBatch = (batchNo: string) => {
    setSelectedBatch(batchNo);
  };

  const handleConfirm = async () => {
    if (!selectedBatch || pickQuantity <= 0) return;

    setConfirming(true);
    try {
      const batch = availableBatches.find(b => b.batchNo === selectedBatch);
      onBatchPicked(selectedBatch, pickQuantity, batch?.expiryDate ?? null);
    } finally {
      setConfirming(false);
    }
  };

  const handleScanInput = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && searchTerm.trim()) {
      // If exact match found, select it
      const exactMatch = availableBatches.find(
        b => b.batchNo.toLowerCase() === searchTerm.trim().toLowerCase()
      );
      if (exactMatch) {
        setSelectedBatch(exactMatch.batchNo);
        // Auto-confirm if remaining qty fits within batch
        if (line) {
          const remaining = line.qty - line.qtyPicked;
          const qty = Math.min(remaining, exactMatch.quantity);
          onBatchPicked(exactMatch.batchNo, qty, exactMatch.expiryDate);
        }
      } else if (filteredBatches.length === 1) {
        setSelectedBatch(filteredBatches[0].batchNo);
        if (line) {
          const remaining = line.qty - line.qtyPicked;
          const qty = Math.min(remaining, filteredBatches[0].quantity);
          onBatchPicked(filteredBatches[0].batchNo, qty, filteredBatches[0].expiryDate);
        }
      } else {
        setError(t('warehouse.modules.picking.batchPick.batchNotAtBin'));
      }
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  const formatExpiryDate = (dateStr: string | null): string => {
    if (!dateStr) return '—';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('nb-NO', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  const isExpiringSoon = (dateStr: string | null): boolean => {
    if (!dateStr) return false;
    try {
      const date = new Date(dateStr);
      const now = new Date();
      const daysUntilExpiry = Math.ceil((date.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
      return daysUntilExpiry <= 30 && daysUntilExpiry >= 0;
    } catch {
      return false;
    }
  };

  const isExpired = (dateStr: string | null): boolean => {
    if (!dateStr) return false;
    try {
      const date = new Date(dateStr);
      return date < new Date();
    } catch {
      return false;
    }
  };

  if (!isOpen || !line) return null;

  const pickedCount = line.qtyPicked;
  const totalRequired = line.qty;
  const remaining = totalRequired - pickedCount;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-0 sm:p-4">
      <div className="bg-white w-full h-full sm:h-auto sm:rounded-lg sm:max-w-lg sm:max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b bg-orange-50">
          <div className="flex items-center gap-2">
            <Package className="text-orange-600" size={24} />
            <h3 className="text-lg font-semibold">{t('warehouse.modules.picking.batchPick.title')}</h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
          >
            <X size={24} />
          </button>
        </div>

        {/* Item info */}
        <div className="p-4 bg-gray-50 border-b">
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div>
              <span className="text-gray-500">{t('common.fields.itemNumber')}:</span>
              <span className="ml-2 font-medium">{line.itemNo}</span>
            </div>
            <div>
              <span className="text-gray-500">{t('warehouse.general.bin')}:</span>
              <span className="ml-2 font-medium">{line.binCode}</span>
            </div>
            <div>
              <span className="text-gray-500">{t('warehouse.modules.picking.picked')}:</span>
              <span className="ml-2 font-medium">{pickedCount} / {totalRequired}</span>
            </div>
            <div>
              <span className="text-gray-500">{t('warehouse.modules.picking.labels.remaining')}:</span>
              <span className="ml-2 font-medium text-orange-600">{remaining}</span>
            </div>
          </div>
          {line.itemDescription && (
            <div className="mt-1 text-sm text-gray-600 truncate">
              {line.itemDescription}
            </div>
          )}
        </div>

        {/* Requested batch info banner */}
        {line.requestedBatchNo && (
          <div className="px-4 py-2 bg-blue-50 border-b border-blue-100 flex items-center gap-2 text-sm text-blue-700">
            <Info size={16} className="flex-shrink-0" />
            <span>
              {t('warehouse.modules.picking.batchPick.expectedBatch')}: <strong className="font-mono">{line.requestedBatchNo}</strong>
              {line.requestedExpiryDate && (
                <span className="ml-2 text-blue-500">
                  ({t('warehouse.modules.picking.batchPick.expiry')}: {formatExpiryDate(line.requestedExpiryDate)})
                </span>
              )}
            </span>
          </div>
        )}

        {/* Search/scan input */}
        <div className="p-4 border-b">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
            <ScanTextInput
              ref={inputRef}
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setError(null);
              }}
              onKeyDown={handleScanInput}
              placeholder={t('warehouse.modules.picking.batchPick.searchPlaceholder')}
              className="w-full pl-10 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              disabled={confirming}
            />
          </div>
          {error && (
            <div className="mt-2 flex items-center gap-2 text-red-600 text-sm">
              <AlertCircle size={16} />
              {error}
            </div>
          )}
        </div>

        {/* Batch list */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center gap-2 text-gray-500 py-8">
              <Loader2 className="animate-spin" size={20} />
              {t('warehouse.modules.picking.batchPick.loadingBatches')}
            </div>
          ) : filteredBatches.length === 0 && !error ? (
            <div className="text-center text-gray-500 py-8">
              {searchTerm ? t('warehouse.modules.picking.batchPick.noSearchMatch') : t('warehouse.modules.picking.batchPick.noBatchesAvailable')}
            </div>
          ) : (
            <div className="space-y-2">
              {filteredBatches.map((batch) => (
                <button
                  key={batch.batchNo}
                  onClick={() => handleSelectBatch(batch.batchNo)}
                  disabled={confirming}
                  className={`w-full flex items-center justify-between p-3 rounded-lg border transition-all text-left ${
                    selectedBatch === batch.batchNo
                      ? 'border-orange-500 bg-orange-50 ring-2 ring-orange-200'
                      : 'border-gray-200 hover:border-orange-300 hover:bg-orange-50/50'
                  }`}
                >
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-2">
                      <span className="font-mono font-medium text-gray-900">
                        {batch.batchNo}
                      </span>
                      {line.requestedBatchNo && batch.batchNo.toLowerCase() === line.requestedBatchNo.toLowerCase() && (
                        <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-medium">
                          {t('warehouse.modules.picking.batchPick.fromPickList')}
                        </span>
                      )}
                      {isExpired(batch.expiryDate) && (
                        <span className="text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded font-medium">
                          {t('warehouse.modules.picking.batchPick.expired')}
                        </span>
                      )}
                      {!isExpired(batch.expiryDate) && isExpiringSoon(batch.expiryDate) && (
                        <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-medium">
                          {t('warehouse.modules.picking.batchPick.expiringSoon')}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-xs text-gray-500">
                      {batch.expiryDate && (
                        <span className="flex items-center gap-1">
                          <Calendar size={12} />
                          {t('warehouse.modules.picking.batchPick.expiry')}: {formatExpiryDate(batch.expiryDate)}
                        </span>
                      )}
                      <span>{t('warehouse.modules.picking.batchPick.available')}: {batch.quantity}</span>
                    </div>
                  </div>
                  {selectedBatch === batch.batchNo && (
                    <CheckCircle className="text-orange-600 flex-shrink-0" size={20} />
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Quantity selector (shown when batch is selected) */}
        {selectedBatch && (
          <div className="px-4 py-3 border-t bg-orange-50">
            <div className="flex items-center gap-3">
              <label className="text-sm font-medium text-gray-700 whitespace-nowrap">{t('warehouse.modules.picking.batchPick.quantityLabel')}:</label>
              <NumericInput
                mode="decimal"
                quantity
                value={pickQuantityInput}
                onChange={(e) => {
                  setPickQuantityInput(e.target.value);
                  const val = parseFloat(e.target.value) || 0;
                  const batch = availableBatches.find(b => b.batchNo === selectedBatch);
                  const maxQty = Math.min(remaining, batch?.quantity ?? 0);
                  setPickQuantity(Math.min(Math.max(0, val), maxQty));
                }}
                min={0}
                max={Math.min(remaining, availableBatches.find(b => b.batchNo === selectedBatch)?.quantity ?? 0)}
                className="w-24 px-3 py-2 border-2 border-orange-300 rounded-lg text-center font-medium focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              />
              <span className="text-sm text-gray-500">
                {t('warehouse.modules.picking.batchPick.ofMax', { 0: Math.min(remaining, availableBatches.find(b => b.batchNo === selectedBatch)?.quantity ?? 0) })}
              </span>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="p-4 border-t bg-gray-50 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
          >
            {t('common.buttons.cancel')}
          </button>
          <button
            onClick={handleConfirm}
            disabled={!selectedBatch || pickQuantity <= 0 || confirming}
            className="flex-1 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors font-medium flex items-center justify-center gap-2"
          >
            {confirming ? (
              <>
                <Loader2 className="animate-spin" size={18} />
                {t('warehouse.modules.picking.batchPick.confirming')}
              </>
            ) : (
              t('warehouse.modules.picking.batchPick.confirmPick', { 0: pickQuantity })
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
